import { useMemo, useState } from 'react';

import { ArrowUpOutlined } from '@ant-design/icons';
import { Input, Radio, Select, Table } from 'antd';
import { T, TF } from 'react-translator-component';

import DirectionBanner from './DirectionBanner';
import {
  connectorBreakdown,
  connectorTypes,
  formatDate,
  formatPower,
  LOCATION_STATUS,
  locationPoints,
  matchesStation,
  maxPower,
  statusOf,
  STATIONS_PAGE_SIZE,
} from './ocpiHelpers';
import type { Navigate } from './OcpiPage';
import OcpiStationMap from './OcpiStationMap';
import StatusTag from './StatusTag';
import * as S from './styled';

import { OcpiLocation, OcpiPage } from '../../../types/responses/Ocpi';

interface Props {
  locations: OcpiPage<OcpiLocation>;
  nav: Navigate;
}

const STATUS_FILTERS = ['AVAILABLE', 'CHARGING', 'BLOCKED', 'OUTOFORDER', 'INOPERATIVE', 'UNKNOWN'];

const StationsTab = ({ locations, nav }: Props) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [state, setState] = useState('all');
  const [view, setView] = useState<'list' | 'map'>('list');

  const states = useMemo(
    () => Array.from(new Set(locations.list.map((l) => l.state).filter(Boolean))).sort() as string[],
    [locations.list],
  );

  const rows = locations.list.filter(
    (l) =>
      matchesStation(l, query) &&
      (status === 'all' || l.status === status) &&
      (state === 'all' || l.state === state),
  );

  const open = (location: OcpiLocation) =>
    location.chargeBoxUuid && nav.open({ kind: 'station', uuid: location.chargeBoxUuid });

  return (
    <>
      <DirectionBanner
        icon={<ArrowUpOutlined />}
        title={T('Sending as operator (CPO)')}
        text={T('Stations of this account that the OCPI service publishes to the partners: public and marked as OCPI compatible.')}
      />
      <S.Panel>
        <S.Toolbar style={{ marginBottom: 0 }}>
          <Input.Search
            allowClear
            style={{ width: 300 }}
            placeholder={TF('Station ID, UUID, address or city')}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Select value={status} onChange={(value) => setStatus(String(value))} style={{ width: 200 }}>
            <Select.Option value="all">{T('All statuses')}</Select.Option>
            {STATUS_FILTERS.map((s) => (
              <Select.Option key={s} value={s}>
                {T(statusOf(LOCATION_STATUS, s).label)}
              </Select.Option>
            ))}
          </Select>
          <Select value={state} onChange={(value) => setState(String(value))} style={{ width: 120 }}>
            <Select.Option value="all">{T('All states')}</Select.Option>
            {states.map((uf) => (
              <Select.Option key={uf} value={uf}>
                {uf}
              </Select.Option>
            ))}
          </Select>
          <span style={{ flexGrow: 1 }} />
          <S.Muted>
            {rows.length} {T('stations')}
          </S.Muted>
          <Radio.Group value={view} onChange={(e) => setView(e.target.value)} buttonStyle="solid">
            <Radio.Button value="list">{T('List')}</Radio.Button>
            <Radio.Button value="map">{T('Map')}</Radio.Button>
          </Radio.Group>
        </S.Toolbar>
        {locations.total > locations.list.length && (
          <S.Muted>
            {T('Showing the first')} {locations.list.length} {T('of')} {locations.total}.
          </S.Muted>
        )}
      </S.Panel>

      {view === 'map' ? (
        <S.Panel>
          <OcpiStationMap points={locationPoints(rows, T)} onSelect={(uuid) => nav.open({ kind: 'station', uuid })} />
        </S.Panel>
      ) : (
        <S.Panel>
          <Table
            rowKey={(l) => l.chargeBoxUuid || `${l.addressPk}-${l.chargeBoxId}`}
            dataSource={rows}
            pagination={{ pageSize: STATIONS_PAGE_SIZE, showSizeChanger: false, hideOnSinglePage: true }}
            onRow={(record) => ({ onClick: () => open(record) })}
            rowClassName={(l) =>
              l.chargeBoxUuid && l.chargeBoxUuid === nav.station ? 'ocpi-row-clickable ocpi-row-selected' : 'ocpi-row-clickable'
            }
            locale={{ emptyText: T('No station with these filters.') }}
            columns={[
              {
                title: T('Station'),
                render: (_, l) => (
                  <>
                    <div style={{ fontWeight: 500 }}>{l.chargeBoxId}</div>
                    <S.Muted>{l.street || '—'}</S.Muted>
                  </>
                ),
              },
              { title: T('City'), render: (_, l) => [l.city, l.state].filter(Boolean).join(' / ') || '—' },
              {
                title: T('Connectors'),
                render: (_, l) => (
                  <>
                    <div>
                      {(l.connectors || []).length} {T('connectors')}
                    </div>
                    <S.Muted>
                      {connectorTypes(l) || '—'} · {T('up to')} {formatPower(maxPower(l))}
                    </S.Muted>
                  </>
                ),
              },
              {
                title: T('Status'),
                render: (_, l) => (
                  <>
                    <StatusTag {...statusOf(LOCATION_STATUS, l.status)} />
                    <div>
                      <S.Muted>{connectorBreakdown(l.connectors, TF)}</S.Muted>
                    </div>
                  </>
                ),
              },
              { title: T('Registered on'), dataIndex: 'latestTimestamp', render: formatDate },
            ]}
          />
        </S.Panel>
      )}
    </>
  );
};

export default StationsTab;
